import React, { useEffect, useRef, useState } from "react";
import APIService from "../../../Service/APIService";
import Text from "../../../common/typography/text.js";
import Button from "../../../common/button/button.js";
import Panel from "../../../common/panel/index.js";
import { colors } from "../../../theme/colors.js";
import { formatCurrency } from "../aabiFormatters";
import ReceivablesDonut from "./ReceivablesDonut.js";
import "./../AABI.css";

// Open Balance and Past Due (§5.5). Operationally different from the scoped
// analytics — these are balances as they stand right now, not activity within
// the selected period — so they live in their own clearly separated panel and
// can never be mistaken for custom-range analytics (§4.2).
//
// The two figures are NOT additive. summarizeOpenBalanceInvoices (BE
// controllers/utils.ts) adds every past-due invoice's balance to `openBalance`
// and then again to `pastDueBalance`, so past due is a SUBSET of open balance.
// The donut therefore splits open balance — the whole — into Past Due and Not
// Yet Due; summing the two figures instead would double-count every past-due
// dollar.
//
// Trust rules (§4.6): a failed fetch shows a visible error with Retry — never
// a valid-looking zero.
const PAST_DUE_COLOR = colors.failureColor;
const NOT_YET_DUE_COLOR = colors.primaryColor;

const ReceivablesSummary = ({ manufacturerId }) => {
  const [homeData, setHomeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const requestIdRef = useRef(0);

  const load = async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(false);
    try {
      const apiServe = new APIService();
      const response = await apiServe.getDataFromBackend(
        `getmanufacturerhomedata?ID=${manufacturerId}`,
      );
      if (requestId !== requestIdRef.current) return;
      setHomeData(response?.data?.data ?? null);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      console.error("Failed to fetch receivables data", err);
      setError(true);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manufacturerId]);

  const openBalance = Number(homeData?.OpenBalanceYTD ?? 0);
  const pastDue = Number(homeData?.PastDueYTD ?? 0);
  // Clamped: a past-due figure larger than the open balance would be a backend
  // inconsistency, and a negative slice would render as a silent gap.
  const notYetDue = Math.max(0, openBalance - pastDue);

  const slices = [
    { key: "pastDue", label: "Past Due", value: pastDue, color: PAST_DUE_COLOR },
    { key: "notYetDue", label: "Not Yet Due", value: notYetDue, color: NOT_YET_DUE_COLOR },
  ];
  const rows = slices.map((slice) => ({
    ...slice,
    valueColor: slice.key === "pastDue" ? "failureColor" : "darkText",
  }));

  return (
    <Panel
      className="aabi-receivables"
      title="Receivables"
      aria-label="Receivables"
      aria-busy={loading}
    >
      {error ? (
        <div role="alert" className="aabi-panel-error">
          <Text variant="smallText" color="failureColor">
            Couldn&apos;t load receivables.
          </Text>
          <Button variant="primary" onClick={load}>
            Retry
          </Button>
        </div>
      ) : loading ? (
        <div className="aabi-trend-skeleton" aria-hidden="true">
          <div className="aabi-kpi-skeleton" />
          <div className="aabi-kpi-skeleton" />
        </div>
      ) : (
        <ReceivablesDonut
          slices={slices}
          total={openBalance}
          rows={rows}
          summaryLabel={`Open balance of ${formatCurrency(openBalance)}, of which ${formatCurrency(pastDue)} is past due.`}
        />
      )}
    </Panel>
  );
};

export default ReceivablesSummary;
